import { useMemo } from 'react';

export default function PageSkeleton() {
  const menuItems = useMemo(() => [...Array(6)], []);
  const cards = useMemo(() => [...Array(3)], []);
  
  return (
    <div className="flex h-screen w-full bg-white dark:bg-slate-900">
      <div className="hidden md:flex md:flex-col md:w-64 border-r border-gray-200 dark:border-slate-700 p-4 space-y-6">
        <div className="h-8 w-32 bg-gray-200 dark:bg-slate-700 rounded-lg animate-pulse-smooth" />
        
        <div className="space-y-3 mt-4">
          {menuItems.map((_, i) => (
            <div 
              key={i}
              className={`h-4 ${
                i % 2 === 0 ? 'w-full' : i % 3 === 0 ? 'w-5/6' : 'w-4/6'
              } bg-gray-200 dark:bg-slate-700 rounded animate-pulse-smooth`}
              style={{ animationDelay: `${i * 60}ms` }}
            />
          ))}
        </div>

        <div className="mt-auto pt-4 border-t border-gray-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-gray-200 dark:bg-slate-700 rounded-full animate-pulse-smooth" />
            <div className="flex-1 space-y-2">
              <div className="h-3 w-24 bg-gray-200 dark:bg-slate-700 rounded animate-pulse-smooth" />
              <div className="h-2 w-16 bg-gray-200 dark:bg-slate-700 rounded animate-pulse-smooth" />
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col flex-1 min-w-0">
        <div className="hidden md:flex items-center justify-between h-16 px-6 border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900">
          <div className="flex items-center gap-4">
            <div className="h-6 w-6 bg-gray-200 dark:bg-slate-700 rounded animate-pulse-smooth" />
            <div className="h-4 w-40 bg-gray-200 dark:bg-slate-700 rounded animate-pulse-smooth" />
          </div>
          <div className="flex items-center space-x-4">
            <div className="h-8 w-8 bg-gray-200 dark:bg-slate-700 rounded-full animate-pulse-smooth" />
            <div className="h-8 w-8 bg-gray-200 dark:bg-slate-700 rounded-full animate-pulse-smooth" />
          </div>
        </div>
        <div className="flex-1 overflow-auto p-4 md:p-6 space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="h-8 w-48 bg-gray-200 dark:bg-slate-700 rounded-lg animate-pulse-smooth" />
              <div className="h-4 w-64 bg-gray-200 dark:bg-slate-700 rounded mt-2 animate-pulse-smooth" />
            </div>
            <div className="h-10 w-36 bg-gray-200 dark:bg-slate-700 rounded-lg animate-pulse-smooth" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div 
                key={i}
                className="bg-gray-200 dark:bg-slate-700 rounded-xl p-4 animate-pulse-smooth"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <div className="h-4 w-20 bg-gray-300 dark:bg-slate-600 rounded mb-2" />
                <div className="h-8 w-16 bg-gray-300 dark:bg-slate-600 rounded" />
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              <div className="bg-gray-200 dark:bg-slate-700 rounded-xl p-4 animate-pulse-smooth">
                <div className="flex items-center justify-between mb-4">
                  <div className="h-5 w-32 bg-gray-300 dark:bg-slate-600 rounded" />
                  <div className="h-8 w-28 bg-gray-300 dark:bg-slate-600 rounded-lg" />
                </div>
                {[...Array(4)].map((_, i) => (
                  <div 
                    key={i}
                    className="flex items-center gap-4 py-3 border-b border-gray-300 dark:border-slate-600 last:border-0"
                  >
                    <div className="h-4 w-24 bg-gray-300 dark:bg-slate-600 rounded" />
                    <div className="h-4 w-32 bg-gray-300 dark:bg-slate-600 rounded" />
                    <div className="h-6 w-16 bg-gray-300 dark:bg-slate-600 rounded-full ml-auto" />
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              {cards.map((_, i) => (
                <div 
                  key={i}
                  className="bg-gray-200 dark:bg-slate-700 rounded-xl p-4 animate-pulse-smooth"
                  style={{ animationDelay: `${i * 120}ms` }}
                >
                  <div className="h-4 w-24 bg-gray-300 dark:bg-slate-600 rounded mb-3" />
                  <div className="space-y-2">
                    <div className="h-3 w-full bg-gray-300 dark:bg-slate-600 rounded" />
                    <div className="h-3 w-5/6 bg-gray-300 dark:bg-slate-600 rounded" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse-smooth {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
        .animate-pulse-smooth {
          animation: pulse-smooth 1.8s ease-in-out infinite;
        }

        @media (prefers-reduced-motion: reduce) {
          .animate-pulse-smooth {
            animation: none;
            opacity: 0.7;
          }
        }

        .animate-pulse-smooth {
          will-change: opacity;
        }
      `}</style>
    </div>
  );
}